import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { LangContext } from '../i18n/LangContext';
import { AuthContext } from '../contexts/AuthContext';
import { getLogoUrl } from '../config';
import { Clock, RefreshCw, CreditCard, CheckCircle, Package, PartyPopper, XCircle, Ban, HelpCircle, FileText, Briefcase, Calendar, BarChart3, MessageCircle, Bell, Info, Send, DollarSign, AlertTriangle, ClipboardList, Pin, Lightbulb, Check } from 'lucide-react';
import { toast } from '../components/Toast';
import logger from '../utils/logger';

const ServiceRequestDetails = () => {
  const { id } = useParams();
  useContext(LangContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const loadRequest = async () => {
    try {
      const res = await api.get(`/service-requests/${id}`);
      setRequest(res.data);
    } catch (err) {
      logger.error(err);
      if (err.response?.status === 403 || err.response?.status === 404) {
        navigate('/my-service-requests');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [request?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await api.post(`/service-requests/${id}/messages`, { message: newMessage });
      setNewMessage('');
      loadRequest(); // Recharger pour avoir le nouveau message
    } catch (err) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const getStatusInfo = (statut) => {
    const statusMap = {
      'en_attente': { label: 'En attente de traitement', labelIcon: <Clock className="w-4 h-4 inline mr-1" />, color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-5 h-5" />, step: 1 },
      'en_cours': { label: 'Traitement en cours', labelIcon: <RefreshCw className="w-4 h-4 inline mr-1" />, color: 'bg-blue-100 text-blue-700', icon: <RefreshCw className="w-5 h-5" />, step: 2 },
      'paiement_attente': { label: 'En attente de paiement', labelIcon: <CreditCard className="w-4 h-4 inline mr-1" />, color: 'bg-orange-100 text-orange-700', icon: <CreditCard className="w-5 h-5" />, step: 3 },
      'paiement_recu': { label: 'Paiement confirmé', labelIcon: <CheckCircle className="w-4 h-4 inline mr-1" />, color: 'bg-teal-100 text-teal-700', icon: <CheckCircle className="w-5 h-5" />, step: 4 },
      'fourniture': { label: 'Service en cours de fourniture', labelIcon: <Package className="w-4 h-4 inline mr-1" />, color: 'bg-purple-100 text-purple-700', icon: <Package className="w-5 h-5" />, step: 5 },
      'termine': { label: 'Terminé avec succès', labelIcon: <PartyPopper className="w-4 h-4 inline mr-1" />, color: 'bg-green-100 text-green-700', icon: <PartyPopper className="w-5 h-5" />, step: 6 },
      'annule': { label: 'Annulé', labelIcon: <XCircle className="w-4 h-4 inline mr-1" />, color: 'bg-gray-100 text-gray-700', icon: <XCircle className="w-5 h-5" />, step: 0 },
      'refuse': { label: 'Refusé', labelIcon: <Ban className="w-4 h-4 inline mr-1" />, color: 'bg-red-100 text-red-700', icon: <Ban className="w-5 h-5" />, step: 0 }
    };
    return statusMap[statut] || { label: statut, labelIcon: <HelpCircle className="w-4 h-4 inline mr-1" />, color: 'bg-gray-100 text-gray-700', icon: <HelpCircle className="w-5 h-5" />, step: 0 };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!request || !request.id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto" />
          <p className="text-gray-600 mt-4">Demande non trouvée</p>
          <Link to="/my-service-requests" className="text-indigo-600 hover:underline mt-2 inline-block">
            Retour à mes demandes
          </Link>
        </div>
      </div>
    );
  }

  // Vérifier si le module service_manager est actif pour le créateur du service
  // Ne pas bloquer si l'utilisateur est le propriétaire du service ou admin
  if (request.service_manager_active === false && !request.is_service_owner && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="text-center max-w-md">
          <Clock className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Service temporairement indisponible</h2>
          <p className="text-gray-600 mb-4">
            Le prestataire n'a pas activé la réception de demandes pour ce service. 
            Vous ne pouvez pas communiquer avec lui pour le moment.
          </p>
          <Link 
            to="/my-service-requests" 
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            ← Retour à mes demandes
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(request.statut_nom);
  const steps = [
    { label: 'Demande reçue', icon: <FileText className="w-5 h-5" />, completed: statusInfo.step >= 1 },
    { label: 'En traitement', icon: <RefreshCw className="w-5 h-5" />, completed: statusInfo.step >= 2 },
    { label: 'Paiement', icon: <CreditCard className="w-5 h-5" />, completed: statusInfo.step >= 4 },
    { label: 'Fourniture', icon: <Package className="w-5 h-5" />, completed: statusInfo.step >= 5 },
    { label: 'Terminé', icon: <Check className="w-5 h-5" />, completed: statusInfo.step >= 6 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 py-10 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/my-service-requests" className="text-indigo-600 hover:underline">
            ← Retour à mes demandes
          </Link>
        </div>

        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            {request.logo ? (
              <img 
                src={getLogoUrl(request.logo)}
                alt={request.service_titre}
                className="w-20 h-20 object-contain bg-gray-100 rounded-xl p-2"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Briefcase className="w-10 h-10 text-white" />
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800 mb-1">{request.titre}</h1>
              <p className="text-gray-500 mb-3">{request.service_titre}</p>
              <div className="flex flex-wrap gap-2">
                <span className={`px-4 py-2 rounded-full font-medium flex items-center ${statusInfo.color}`}>
                  {statusInfo.labelIcon}{statusInfo.label}
                </span>
                <span className="px-4 py-2 bg-gray-100 text-gray-600 rounded-full flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {formatDate(request.date_demande)}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Référence</p>
              <p className="text-lg font-mono font-bold text-indigo-600">#{request.id.toString().padStart(5, '0')}</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        {statusInfo.step > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Progression</h2>
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      step.completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {step.completed ? <Check className="w-5 h-5" /> : step.icon}
                    </div>
                    <p className={`text-xs mt-2 text-center ${step.completed ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                      {step.label}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      steps[index + 1].completed ? 'bg-green-500' : 'bg-gray-200'
                    }`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><FileText className="w-5 h-5" /> Détails de la demande</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-line">{request.description}</p>
                {request.details_supplementaires && (
                  <>
                    <h3 className="text-md font-semibold text-gray-700 mt-4 mb-2">Informations complémentaires:</h3>
                    <p className="text-gray-600 whitespace-pre-line">{request.details_supplementaires}</p>
                  </>
                )}
              </div>
            </div>

            {/* Messages / Chat */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Conversation
                  {request.messages?.filter(m => !m.lu && m.sender_id !== user?.id).length > 0 && (
                    <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                      Nouveau
                    </span>
                  )}
                </h2>
              </div>
              
              {/* Messages List */}
              <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {request.messages && request.messages.length > 0 ? (
                  request.messages.map((msg, index) => {
                    const isMe = msg.sender_id === user?.id;
                    const isSystem = msg.type === 'systeme' || msg.type === 'notification';
                    
                    if (isSystem) {
                      return (
                        <div key={index} className="text-center">
                          <span className="inline-flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-600 text-sm rounded-full">
                            {msg.type === 'notification' ? <Bell className="w-4 h-4" /> : <Info className="w-4 h-4" />}{msg.message}
                          </span>
                          <p className="text-xs text-gray-400 mt-1">{formatDate(msg.created_at)}</p>
                        </div>
                      );
                    }
                    
                    return (
                      <div key={index} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] ${isMe ? 'order-2' : 'order-1'}`}>
                          <div className={`p-4 rounded-2xl ${
                            isMe 
                              ? 'bg-indigo-600 text-white rounded-br-md' 
                              : 'bg-white shadow text-gray-800 rounded-bl-md'
                          }`}>
                            {!isMe && (
                              <p className="text-xs font-medium text-indigo-600 mb-1">
                                {msg.sender_nom} ({msg.sender_role === 'admin' ? 'Support' : 'Utilisateur'})
                              </p>
                            )}
                            <p className="whitespace-pre-line">{msg.message}</p>
                          </div>
                          <p className={`text-xs text-gray-400 mt-1 ${isMe ? 'text-right' : 'text-left'}`}>
                            {formatDate(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>Aucun message pour le moment</p>
                    <p className="text-sm">Posez une question si vous avez besoin d'aide</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Message Input */}
              <form onSubmit={sendMessage} className="p-4 border-t bg-white">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
                  >
                    {sending ? '...' : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment Info */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5" /> Facturation</h2>
              
              {request.montant_final ? (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Montant total</span>
                    <span className="font-bold text-xl">{Number(request.montant_final).toLocaleString()} XOF</span>
                  </div>
                  {request.montant_paye > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Payé</span>
                      <span className="font-medium">-{Number(request.montant_paye).toLocaleString()} XOF</span>
                    </div>
                  )}
                  <hr />
                  <div className="flex justify-between">
                    <span className="font-medium">Reste à payer</span>
                    <span className="font-bold text-xl text-indigo-600">
                      {Number(request.montant_final - (request.montant_paye || 0)).toLocaleString()} XOF
                    </span>
                  </div>
                  
                  {request.statut_nom === 'paiement_attente' && (
                    <div className="mt-4 p-4 bg-orange-50 rounded-xl">
                      <p className="text-orange-800 text-sm font-medium mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Paiement en attente</p>
                      <p className="text-orange-600 text-xs">
                        Veuillez procéder au paiement pour que nous puissions continuer le traitement de votre demande.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <ClipboardList className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500 text-sm">Devis en cours de préparation</p>
                  <p className="text-gray-400 text-xs mt-1">Vous serez notifié dès qu'il sera prêt</p>
                </div>
              )}
            </div>

            {/* Request Info */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Pin className="w-5 h-5" /> Informations</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Priorité</span>
                  <span className="font-medium flex items-center gap-1">
                    <span className={`w-3 h-3 rounded-full ${request.priorite === 'urgente' ? 'bg-red-500' : request.priorite === 'haute' ? 'bg-orange-500' : request.priorite === 'normale' ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
                    {request.priorite}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Catégorie</span>
                  <span className="font-medium">{request.categorie || '-'}</span>
                </div>
                {request.date_fin_prevue && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date souhaitée</span>
                    <span className="font-medium">{new Date(request.date_fin_prevue).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
                {request.assigne_nom && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Responsable</span>
                    <span className="font-medium">{request.assigne_nom}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Help Card */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
              <h3 className="font-bold mb-2 flex items-center gap-2"><Lightbulb className="w-5 h-5" /> Besoin d'aide ?</h3>
              <p className="text-sm text-indigo-100 mb-4">
                Notre équipe est disponible pour répondre à vos questions via la messagerie ci-contre.
              </p>
              <p className="text-xs text-indigo-200">
                Temps de réponse moyen : 24h
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestDetails;
